import {
  Box,
  Typography,
  TextField,
  Popover,
  Button,
  Autocomplete,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@mui/material";
import { useCallback } from "react";
import { underscoreToTitleCase } from "@utils/index";
import type { ReportFilterOptions } from "@hooks/use-report-filter";
import { useReportFilterStateContext } from "@hooks/use-report-filter";
import { useAssetsConfigContext } from "@hooks/use-config-context/asset";
import { isFieldDate, isFieldBoolean } from "@hooks/use-config/utils";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { parseDateInput } from "@utils/format-utils";

export interface ReportFilterPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  available: ReportFilterOptions;
  /**
   * Optional: List of enabled filter field names for this report.
   * If provided, only these fields are shown in the filter panel.
   * If omitted (backward compatibility), shows all available fields.
   */
  enabledFields?: readonly string[];
}

export const ReportFilterPanel = ({
  open,
  anchorEl,
  onClose,
  available,
  enabledFields: enabledFieldsProp,
}: ReportFilterPanelProps) => {
  // Get filter state and setters from context
  const { filterState, createFilterSetter, clearAll } = useReportFilterStateContext();
  const { config: assetsConfig } = useAssetsConfigContext();
  const { setWarning } = useScreenAlert();

  // Use prop if provided, otherwise show all fields in config (backward compatibility)
  const enabledFields = enabledFieldsProp ?? Object.keys(assetsConfig);

  // Handle select/autocomplete change - close if value is not empty
  const handleSelectChange = useCallback(
    (value: string[]) => {
      if (value.length > 0) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle date change - validate range, then close if both dates are filled
  const handleDateChange = useCallback(
    (newValue: { start: string | null; end: string | null }) => {
      if (newValue.start && newValue.end) {
        onClose();
      }
    },
    [onClose]
  );

  // Handle boolean change - close when selection is made
  const handleBooleanChange = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Box sx={{ p: 2, minWidth: 340 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Report Filters
        </Typography>

        {/* Render Filters Dynamically Based on Field Type */}
        {enabledFields.map((columnName) => {
          const fieldConfig = assetsConfig?.[columnName];
          // Skip only if there's no field config AND no available options to show
          if (!fieldConfig && !available[columnName]?.length) return null;

          const label = fieldConfig?.name || underscoreToTitleCase(columnName);
          const currentValue = filterState[columnName];
          const setter = createFilterSetter(columnName);

          // RENDER DATE FILTER — only when we have type metadata
          if (fieldConfig && isFieldDate(fieldConfig)) {
            if (typeof currentValue !== "object" || !("start" in currentValue && "end" in currentValue)) {
              return null;
            }

            return (
              <Box key={columnName}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <TextField
                    type="date"
                    size="small"
                    label="Start"
                    value={currentValue.start ?? ""}
                    onChange={(e) => {
                      const newValue = { 
                        ...currentValue, 
                        start: e.target.value || null 
                      };
                      const startDate = parseDateInput(newValue.start);
                      const endDate = parseDateInput(newValue.end);
                      if (startDate && endDate && startDate > endDate) {
                        setWarning("Start Date must be before End Date");
                        return;
                      }
                      setter(newValue);
                      handleDateChange(newValue);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    to
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    label="End"
                    value={currentValue.end ?? ""}
                    onChange={(e) => {
                      const newValue = { 
                        ...currentValue, 
                        end: e.target.value || null 
                      };
                      const startDate = parseDateInput(newValue.start);
                      const endDate = parseDateInput(newValue.end);
                      if (startDate && endDate && startDate > endDate) {
                        setWarning("Start Date must be before End Date");
                        return;
                      }
                      setter(newValue);
                      handleDateChange(newValue);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </Box>
            );
          }

          // RENDER BOOLEAN FILTER — only when we have type metadata
          if (fieldConfig && isFieldBoolean(fieldConfig)) {
            if (!Array.isArray(currentValue)) return null;

            return (
              <Box key={columnName} sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {label}
                </Typography>
                <RadioGroup
                  value={currentValue[0] ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setter(value ? [value] : []);
                    handleBooleanChange();
                  }}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio size="small" />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    control={<Radio size="small" />}
                    label="No"
                  />
                </RadioGroup>
              </Box>
            );
          }

          // RENDER SELECT/AUTOCOMPLETE FILTER
          const options = available[columnName];
          if (!Array.isArray(currentValue)) return null;

          return (
            <Box key={columnName}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                {label}
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={options || []}
                getOptionLabel={(option) => {
                  return columnName === "asset_site" ? option : underscoreToTitleCase(option);
                }}
                value={currentValue}
                onChange={(_, value) => {
                  setter(value);
                  handleSelectChange(value);
                }}
                disabled={!options || options.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      !options || options.length === 0
                        ? "Not available for this report"
                        : !currentValue || currentValue.length === 0
                        ? `All ${label}${label.endsWith('s') ? 'es' : 's'}`
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Box>
          );
        })}

        <Button 
          size="small" 
          onClick={clearAll} 
          disabled={Object.values(filterState).every(v => 
            Array.isArray(v) ? v.length === 0 : (v.start === null && v.end === null)
          )}
        >
          Clear All
        </Button>
      </Box>
    </Popover>
  );
};
